/**
 * Health-reminder wording and schedule helpers, shared by the Reminders page and its form.
 * Pure (no React), so the wording is unit-tested (tests/unit/reminders.test.mjs).
 *
 * The slot and meal defaults match the backend's (services/health_reminder_service.py).
 */

import type { AlertStyle, FoodRelation, HealthReminder, MealType, ReminderKind } from "../api/reminders";

export const KIND_META: Record<ReminderKind, { label: string; icon: string }> = {
  medicine: { label: "Medicine", icon: "💊" },
  water: { label: "Water", icon: "💧" },
  meal: { label: "Meal", icon: "🍽️" },
};

export const MEALS: { type: MealType; label: string; time: string }[] = [
  { type: "breakfast", label: "Breakfast", time: "08:30" },
  { type: "lunch", label: "Lunch", time: "13:00" },
  { type: "snacks", label: "Evening snacks", time: "17:00" },
  { type: "dinner", label: "Dinner", time: "20:30" },
];

/** Morning / Afternoon / Evening / Night: the slots a prescription uses, at their default times. */
export const SLOTS: { key: string; label: string; time: string }[] = [
  { key: "morning", label: "Morning", time: "08:00" },
  { key: "afternoon", label: "Afternoon", time: "14:00" },
  { key: "evening", label: "Evening", time: "18:00" },
  { key: "night", label: "Night", time: "21:00" },
];

export const WATER_INTERVALS: { minutes: 30 | 60 | 120; label: string }[] = [
  { minutes: 30, label: "30 min" },
  { minutes: 60, label: "1 hour" },
  { minutes: 120, label: "2 hours" },
];

export const ALERT_STYLES: { style: AlertStyle; label: string; icon: string; hint: string }[] = [
  { style: "sound", label: "Sound", icon: "🔔", hint: "Plays a sound and pops up" },
  { style: "vibrate", label: "Vibrate", icon: "📳", hint: "Vibrates, no sound (phone)" },
  { style: "silent", label: "Silent", icon: "🔕", hint: "Arrives quietly in your notifications" },
];

export const FOOD_LABELS: Record<FoodRelation, string> = {
  before_food: "Before food",
  after_food: "After food",
};

export const mealLabel = (type: MealType | null): string =>
  MEALS.find((m) => m.type === type)?.label ?? "Meal";

/** "21:05" → "9:05 PM". Anything that isn't HH:MM is returned as it came. */
export function formatTime12(hhmm: string): string {
  const m = /^(\d{1,2}):(\d{2})/.exec(hhmm);
  if (!m) return hhmm;
  const h = Number(m[1]);
  const suffix = h < 12 ? "AM" : "PM";
  return `${h % 12 === 0 ? 12 : h % 12}:${m[2]} ${suffix}`;
}

/** The schedule in a line: "Every 30 min · 8:00 AM – 10:00 PM" or "Daily · 8:00 AM, 9:00 PM". */
export function scheduleSummary(r: Pick<HealthReminder, "interval_minutes" | "window_start" | "window_end" | "times">): string {
  if (r.interval_minutes && r.window_start && r.window_end) {
    const every = WATER_INTERVALS.find((w) => w.minutes === r.interval_minutes)?.label ?? `${r.interval_minutes} min`;
    return `Every ${every} · ${formatTime12(r.window_start)} – ${formatTime12(r.window_end)}`;
  }
  return `Daily · ${r.times.map(formatTime12).join(", ")}`;
}

/** What a reminder is about, under its title: dosage, food, who for; or the note. */
export function reminderDetail(r: HealthReminder): string {
  if (r.kind === "medicine") {
    const parts = [r.dosage, r.food_relation ? FOOD_LABELS[r.food_relation].toLowerCase() : null];
    if (r.for_name) parts.push(`for ${r.for_name}`);
    return parts.filter(Boolean).join(" · ") || r.note || "";
  }
  if (r.kind === "water" && r.daily_goal_ml) {
    return [`Goal ${(r.daily_goal_ml / 1000).toLocaleString("en-IN", { maximumFractionDigits: 2 })} L a day`, r.note]
      .filter(Boolean)
      .join(" · ");
  }
  return r.note ?? "";
}

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** When the next reminder goes off: "Today, 10:30 AM", "Tomorrow, 8:00 AM" or "Mon 3 Nov, 8:00 AM". */
export function nextLabel(iso: string, now: Date = new Date()): string {
  const at = new Date(iso);
  // Built by hand: toLocaleTimeString puts a narrow no-break space before AM/PM on newer ICUs.
  const time = formatTime12(`${at.getHours()}:${String(at.getMinutes()).padStart(2, "0")}`);
  if (sameDay(at, now)) return `Today, ${time}`;
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  if (sameDay(at, tomorrow)) return `Tomorrow, ${time}`;
  const day = at.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
  return `${day}, ${time}`;
}

/** on: scheduled · off: switched off · ended: on, but its course is over. */
export function reminderState(r: Pick<HealthReminder, "enabled" | "next_fire_at">): "on" | "off" | "ended" {
  if (!r.enabled) return "off";
  return r.next_fire_at ? "on" : "ended";
}

/** The browser's calendar date as YYYY-MM-DD (the water log's day). */
export function localDay(d: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
